/**
 * Top-level page chrome for the CRO scorecard SPA: header (logo + title + freshness pill + log-out),
 * main outlet, footer with EntraPrincipalChip (ENTRA-02 requirement).
 *
 * Modified from packages/spa/src/components/AppShell.tsx:
 * - Logo link: `to='/'` (CRO scorecard root)
 * - Brand title: "Sales Dashboard · CRO View"
 * - Footer: renders <EntraPrincipalChip /> per UI-SPEC §"Authenticated-principal indicator"
 *
 * The Adeptia navy gradient mirrors the mockup's `--adeptia-navy → --adeptia-navy-2`
 * header band so the executive's first impression matches the established
 * brand reference (mockups/dashboard/index.html).
 *
 * Plan 04 UX-08 — mobile-responsive freshness pill placement:
 *   - Desktop (md+): freshness slot lives in the top-right of the header
 *     gradient strip (`hidden md:flex`).
 *   - Mobile (<md): freshness slot drops below the header gradient into a
 *     light bar above `<main>` so the navy band stays compact for thumbs
 *     (`flex md:hidden`).
 *   - Both slots receive the SAME element; the parent decides visibility
 *     via Tailwind responsive utilities.
 */
import { type ReactNode } from 'react';
import { Link, Outlet } from 'react-router';

import { EntraPrincipalChip } from './EntraPrincipalChip.js';
import { Button } from './ui/Button.js';

interface AppShellProps {
  /** Optional override for the freshness pill slot in the header. */
  freshnessSlot?: ReactNode;
  /** Optional user display name shown next to "Log out". */
  userDisplayName?: string;
  /**
   * Optional suffix appended after "Sales Dashboard ·" in the header brand
   * title. When omitted or empty, the title renders as just "Sales Dashboard".
   * e.g., "CRO View" for the CRO scorecard, "Finance Upload" for admin pages.
   */
  brandSuffix?: string;
  /**
   * Optional cross-link affordance in the header right side.
   * e.g., { label: '← CRO scorecard', to: '/' } from /main
   *     or { label: 'Main scorecard ↗', to: '/main' } from / (CRO scorecard)
   */
  crossLink?: { label: string; to: string };
  /**
   * Optional additional header links, rendered alongside `crossLink` with the
   * same styling. Additive — pages that only set `crossLink` are unaffected.
   * Used to surface sibling tabs (e.g. the 2027 Plan) for discoverability.
   */
  extraLinks?: { label: string; to: string }[];
  children?: ReactNode;
}

export function AppShell({
  freshnessSlot,
  userDisplayName,
  brandSuffix,
  crossLink,
  extraLinks,
  children,
}: AppShellProps): React.JSX.Element {
  return (
    <div className="min-h-screen bg-ink-50 text-ink-900">
      <header
        className="text-white shadow-md"
        style={{
          background:
            'linear-gradient(95deg, var(--color-adeptia-navy) 0%, var(--color-adeptia-navy-2) 100%)',
        }}
      >
        <div className="mx-auto flex max-w-[1536px] items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <span
              aria-hidden
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-adeptia-cyan font-bold text-adeptia-cyan"
            >
              A
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-adeptia-cyan">Adeptia</p>
              <h1 className="text-base font-semibold leading-tight">
                {brandSuffix ? `Sales Dashboard · ${brandSuffix}` : 'Sales Dashboard'}
              </h1>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {crossLink && (
              <Link
                to={crossLink.to}
                className="hidden text-xs text-white/80 hover:text-white sm:inline"
                data-testid="app-shell-cross-link"
              >
                {crossLink.label}
              </Link>
            )}
            {extraLinks?.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="hidden text-xs text-white/80 hover:text-white sm:inline"
                data-testid="app-shell-extra-link"
              >
                {l.label}
              </Link>
            ))}
            {freshnessSlot && (
              <span
                data-testid="freshness-slot-desktop"
                className="hidden md:inline-flex"
              >
                {freshnessSlot}
              </span>
            )}
            {userDisplayName && (
              <span className="hidden text-xs text-white/80 sm:inline" data-testid="user-name">
                {userDisplayName}
              </span>
            )}
            <Link to="/logout">
              <Button variant="secondary" size="sm">
                Log out
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile-only freshness bar — keeps the navy header compact on phones
          while still surfacing the freshness signal above the fold. */}
      {freshnessSlot && (
        <div
          data-testid="freshness-slot-mobile"
          className="flex items-center gap-2 border-b border-ink-200 bg-white px-6 py-2 text-xs text-ink-500 md:hidden"
        >
          <span className="font-medium text-ink-700">Data freshness:</span>
          {freshnessSlot}
        </div>
      )}

      <main className="mx-auto max-w-[1536px] px-8 py-6">{children ?? <Outlet />}</main>

      <footer className="mx-auto max-w-[1536px] px-8 py-4 text-xs text-ink-500">
        {/* EntraPrincipalChip calls /api/cro/me which requires Easy Auth.
            Apex (salesdashboard.adeptia.org) has no Easy Auth gate — only
            SF OAuth — so the chip 401s and bounces the user to a non-
            existent /.auth/login/aad. Only render on cro subdomain. */}
        {typeof window !== 'undefined' &&
          window.location.hostname.startsWith('cro.') && <EntraPrincipalChip />}
      </footer>
    </div>
  );
}
